import { useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from '@/components/navigation/Sidebar';
import { Header } from '@/components/navigation/Header';
import { Breadcrumb } from '@/components/navigation/Breadcrumb';
import { ROUTES } from '@/constants';

const breadcrumbMap = [
  { match: ROUTES.DASHBOARD, items: [{ label: 'Dashboard' }] },
  {
    match: ROUTES.ACCOUNTS_PAYABLE,
    items: [{ label: 'Financeiro' }, { label: 'Contas a pagar' }],
  },
  {
    match: ROUTES.ACCOUNTS_RECEIVABLE,
    items: [{ label: 'Financeiro' }, { label: 'Contas a receber' }],
  },
  { match: ROUTES.CASH_FLOW, items: [{ label: 'Financeiro' }, { label: 'Fluxo de caixa' }] },
  {
    match: ROUTES.TRANSACTIONS,
    items: [{ label: 'Financeiro' }, { label: 'Movimentações' }],
  },
  {
    match: ROUTES.BANK_ACCOUNTS,
    items: [{ label: 'Financeiro' }, { label: 'Contas bancárias' }],
  },
  { match: ROUTES.CLIENTS, items: [{ label: 'Cadastros' }, { label: 'Clientes' }] },
  { match: ROUTES.SUPPLIERS, items: [{ label: 'Cadastros' }, { label: 'Fornecedores' }] },
  {
    match: ROUTES.PRODUCTS,
    items: [{ label: 'Cadastros' }, { label: 'Produtos e serviços' }],
  },
  { match: ROUTES.CATEGORIES, items: [{ label: 'Cadastros' }, { label: 'Categorias' }] },
  { match: ROUTES.PRODUCT_TYPES, items: [{ label: 'Cadastros' }, { label: 'Tipos' }] },
  {
    match: ROUTES.COST_CENTERS,
    items: [{ label: 'Cadastros' }, { label: 'Centros de custo' }],
  },
  { match: ROUTES.CONTRACTS, items: [{ label: 'Contratos' }] },
  {
    match: ROUTES.CONTRACT_ADJUSTMENTS,
    items: [{ label: 'Contratos', to: ROUTES.CONTRACTS }, { label: 'Reajustes' }],
  },
  {
    match: ROUTES.CONTRACT_INDICES,
    items: [{ label: 'Contratos', to: ROUTES.CONTRACTS }, { label: 'Índices' }],
  },
  { match: ROUTES.BILLING, items: [{ label: 'Fiscal' }, { label: 'Faturamento' }] },
  { match: ROUTES.INVOICES, items: [{ label: 'Fiscal' }, { label: 'Notas fiscais (NFe / NFS-e)' }] },
  { match: ROUTES.DOCUMENTS, items: [{ label: 'Gestão' }, { label: 'Documentos' }] },
  { match: ROUTES.PASSWORDS, items: [{ label: 'Gestão' }, { label: 'Cofre de senhas' }] },
  { match: ROUTES.REPORTS, items: [{ label: 'Gestão' }, { label: 'Relatórios' }] },
  { match: ROUTES.SETTINGS, items: [{ label: 'Configurações' }] },
];

function resolveBreadcrumb(pathname) {
  if (pathname.startsWith('/cadastros/clientes/') && pathname !== ROUTES.CLIENTS) {
    return [
      { label: 'Cadastros', to: ROUTES.CLIENTS },
      { label: 'Clientes', to: ROUTES.CLIENTS },
      { label: 'Detalhes' },
    ];
  }
  if (pathname.startsWith('/cadastros/fornecedores/') && pathname !== ROUTES.SUPPLIERS) {
    return [
      { label: 'Cadastros', to: ROUTES.SUPPLIERS },
      { label: 'Fornecedores', to: ROUTES.SUPPLIERS },
      { label: 'Detalhes' },
    ];
  }
  const found = breadcrumbMap.find((item) => item.match === pathname);
  return found?.items || [{ label: '3LA Manager' }];
}

export function DashboardLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const breadcrumbItems = useMemo(
    () => resolveBreadcrumb(location.pathname),
    [location.pathname],
  );

  return (
    <div className="flex h-dvh overflow-hidden bg-[var(--background)]">
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((value) => !value)}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <Header
          onOpenMobileSidebar={() => setMobileOpen(true)}
          breadcrumb={<Breadcrumb items={breadcrumbItems} />}
        />
        <main className="min-h-0 flex-1 overflow-y-auto p-4 md:p-6">
          <div className="mb-4 md:hidden">
            <Breadcrumb items={breadcrumbItems} />
          </div>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
